import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AlertTriangle, CheckCircle2, RefreshCw, ShieldAlert } from "lucide-react";

// Owner-only pre-flight guard shown before a PAID scan: warns if the AI master switch is OFF /
// budget-paused, or if durable photo storage (B2) is unreachable. Silently hides for anyone
// without the AI-budget permission (endpoint 403s). Auto-checks when `active` becomes true.
export function PreflightBanner({ active = true }) {
  // ONLY ASK WHAT THIS ROLE MAY SEE (loose end, 22 Oct): without 'Manage AI budget' the check is never sent,
  // so no refused request lands in the browser console for a role that can't see the banner anyway.
  const { has } = useAuth();
  const mayAsk = has("manage_ai_budget");
  const [state, setState] = useState(null);   // null = unknown/hidden
  const [busy, setBusy] = useState(false);

  const check = useCallback(async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/ai/preflight");
      setState(data);
    } catch (e) {
      setState(null);   // 403 (not an owner) or error → show nothing
    } finally { setBusy(false); }
  }, []);

  useEffect(() => { if (active && mayAsk) check(); }, [active, mayAsk, check]);

  if (!mayAsk || !state) return null;

  if (state.ok) {
    return (
      <div data-testid="preflight-ok" className="flex items-center gap-2 px-4 py-1.5 bg-emerald-50 border-b border-emerald-200 text-emerald-800 text-xs shrink-0">
        <CheckCircle2 className="w-3.5 h-3.5 shrink-0" />
        <span>Pre-flight clear — AI is on and photo storage is reachable. Safe to scan.</span>
        <button data-testid="preflight-recheck" onClick={check} disabled={busy} className="ml-auto inline-flex items-center gap-1 underline hover:no-underline">
          <RefreshCw className={`w-3 h-3 ${busy ? "animate-spin" : ""}`} />re-check
        </button>
      </div>
    );
  }

  const hasBlocker = state.warnings.some((w) => w.level === "blocker");
  return (
    <div data-testid="preflight-warning" className={`px-4 py-2 border-b shrink-0 ${hasBlocker ? "bg-red-50 border-red-200" : "bg-amber-50 border-amber-200"}`}>
      <div className="flex items-start gap-2">
        {hasBlocker ? <ShieldAlert className="w-4 h-4 text-[#B01E28] shrink-0 mt-0.5" /> : <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />}
        <div className="flex-1 min-w-0">
          <div className={`text-xs font-bold ${hasBlocker ? "text-[#B01E28]" : "text-amber-800"}`}>
            {hasBlocker ? "Don't start a paid scan yet" : "Heads up before you scan"}
          </div>
          <ul className="mt-0.5 space-y-0.5">
            {state.warnings.map((w) => (
              <li key={w.code} data-testid={`preflight-${w.code}`} className="text-xs text-slate-700 flex items-start gap-1.5">
                <span className={w.level === "blocker" ? "text-[#B01E28]" : "text-amber-600"}>•</span>{w.msg}
              </li>
            ))}
          </ul>
        </div>
        <button data-testid="preflight-recheck" onClick={check} disabled={busy} className="shrink-0 inline-flex items-center gap-1 text-xs underline text-slate-600 hover:no-underline">
          <RefreshCw className={`w-3 h-3 ${busy ? "animate-spin" : ""}`} />re-check
        </button>
      </div>
    </div>
  );
}
